<template>
  <div>
    <!-- User Information Section -->
    <UserInformationSection
      :form="form"
      :name-options="nameOptions"
      :ugroups="ugroups"
      @update-form="updateForm"
      @change-group="changeGroup"
    />

    <!-- Contact Information Section -->
    <ContactInformationSection
      :form="form"
      :phone-edit-enabled="phoneEditEnabled"
      :email-edit-enabled="emailEditEnabled"
      :is-any-field-editing="isAnyFieldEditing"
      @update-form="updateForm"
      @enable-field-edit="enableFieldEdit"
      @cancel-field-edit="cancelFieldEdit"
      @save-field-edit="saveFieldEdit"
    />

    <!-- Access Verification Section -->
    <AccessVerificationSection
      v-if="!form.isPhoneVerified || !form.isEmailVerified"
      :form="form"
      @update-form="updateForm"
    />

    <!-- Driver Password Section - Only show for Driver -->
    <DriverPasswordSection
      v-if="form.isDriver"
      :form="form"
      :show-password="showPassword"
      :show-repassword="showRepassword"
      :show-tooltip-password="showTooltipPassword"
      :show-tooltip-repassword="showTooltipRepassword"
      :password-validations-cases="passwordValidationsCases"
      :is-password-matched="isPasswordMatched"
      @update-form="updateForm"
      @toggle-password="togglePassword"
      @toggle-repassword="toggleRepassword"
      @show-tooltip="onShowTooltip"
      @hide-tooltip="onHideTooltip"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { Helper } from '@/mixin/helper'
import UserInformationSection from '../section/UserInformationSection.vue'
import ContactInformationSection from '../section/ContactInformationSection.vue'
import AccessVerificationSection from '../section/AccessVerificationSection.vue'
import DriverPasswordSection from '../section/DriverPasswordSection.vue'
import { GET_USER_GROUP } from '@/services/api/setting/user/userGroup'

export default {
  name: 'EditUserForm',

  components: {
    UserInformationSection,
    ContactInformationSection,
    AccessVerificationSection,
    DriverPasswordSection,
  },

  mixins: [Helper],

  apollo: {
    ugroups: {
      fetchPolicy: 'no-cache',
      query: GET_USER_GROUP,
      variables() {
        return {
          filter: {
            pagination: {
              page: 1,
              show: 9999,
            },
            showSuperAdmin: false,
            showSuperUserOperational:
              this.hasAccessAll([
                'tms-user-group:create',
                'tms-user-group:delete',
                'tms-user-group:read',
                'tms-user-group:update',
              ])
          },
        }
      },
      result({ data, loading }) {
        if (!loading) {
          this.ugroups.ugroups = data.ugroups.ugroups.data
        }
      },
    },
  },

  props: {
    form: {
      type: Object,
      required: true,
    },
    nameOptions: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      phoneEditEnabled: false,
      emailEditEnabled: false,
      showPassword: false,
      showRepassword: false,
      showTooltipPassword: false,
      showTooltipRepassword: false,
      passwordValidationsCases: [
        'lowerCase',
        'specialCharacter',
        'upperCase',
        'minimumLength',
        'number',
      ],
      ugroups: {
        ugroups: [],
      },
      oldPhone: '', // simpan nilai awal
      oldEmail: '', // simpan nilai awal
    }
  },

  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    isAnyFieldEditing() {
      return this.phoneEditEnabled || this.emailEditEnabled
    },

    isPasswordMatched() {
      return (
        this.form.password && this.form.repassword && this.form.password === this.form.repassword
      )
    },
  },

  mounted() {
    // simpan nilai awal dari form
    this.oldPhone = this.form.phoneNo
    this.oldEmail = this.form.email
  },

  methods: {
    updateForm(updates) {
      this.$emit('update-form', updates)
    },

    enableFieldEdit(field) {
      if (field === 'phone') {
        this.phoneEditEnabled = true
      } else if (field === 'email') {
        this.emailEditEnabled = true
      }

      // Tambahkan ini sebelum emit update-form:
      this.$emit('field-change', 'isAnyFieldEditing', this.isAnyFieldEditing)
    },

    cancelFieldEdit(field) {
      let newForm = { ...this.form }

      if (field === 'phone') {
        this.phoneEditEnabled = false
        // reset ke nilai awal
        newForm.phoneNo = this.oldPhone
      } else if (field === 'email') {
        this.emailEditEnabled = false
        // reset ke nilai awal
        newForm.email = this.oldEmail
      }

      // emit supaya form sinkron
      this.$emit('update-form', newForm)
      this.$emit('field-change', 'isAnyFieldEditing', this.isAnyFieldEditing)
    },

    saveFieldEdit(field) {
      let newForm = { ...this.form }

      if (field === 'phone') {
        if (newForm.phoneNo !== this.oldPhone) {
          newForm.isPhoneVerified = false
          this.oldPhone = newForm.phoneNo
        }
        this.phoneEditEnabled = false
      } else if (field === 'email') {
        if (newForm.email !== this.oldEmail) {
          newForm.isEmailVerified = false
          this.oldEmail = newForm.email
        }
        this.emailEditEnabled = false
      }

      // Emit update:form
      this.$emit('update-form', newForm)
      this.$emit('field-change', 'isAnyFieldEditing', this.isAnyFieldEditing)
    },

    changeGroup(value) {
      let newForm = { ...this.form }
      newForm.groups = value

      // emit supaya form sinkron
      this.$emit('update-form', newForm)
    },

    togglePassword() {
      this.showPassword = !this.showPassword
    },

    toggleRepassword() {
      this.showRepassword = !this.showRepassword
    },

    onShowTooltip({ key }) {
      if (key === 'Password') {
        this.showTooltipPassword = true
      } else if (key === 'Repassword') {
        this.showTooltipRepassword = true
      }
    },

    onHideTooltip({ key }) {
      if (key === 'Password') {
        this.showTooltipPassword = false
      } else if (key === 'Repassword') {
        this.showTooltipRepassword = false
      }
    },
  },
}
</script>
